import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';
interface Testimonial {
  name: string;
  quote: string;
  location: string;
}
const TESTIMONIALS: Testimonial[] = [{
  name: 'María Fernández',
  quote: 'En las primeras 72 horas ya sentí cómo se me desinflamaba la panza. A los pocos días pude volver a cerrar ese pantalón que tenía guardado hace meses. Nunca pensé que un cambio tan simple iba a hacer semejante diferencia.',
  location: 'Buenos Aires, Argentina'
}, {
  name: 'Sofía Martínez',
  quote: 'Vivía hinchada y pesada todo el día, sobre todo después de comer. Con el protocolo entendí qué me estaba inflamando y en menos de una semana me desperté liviana por primera vez en años. ¡Recomendado 100%!',
  location: 'Córdoba, Argentina'
}, {
  name: 'Valentina López',
  quote: 'Lo que más me sorprendió fue lo fácil que es seguirlo, sin pasar hambre ni dietas imposibles. La retención de líquidos bajó muchísimo y mi abdomen se ve y se siente completamente distinto.',
  location: 'Rosario, Argentina'
}, {
  name: 'Catalina Rodríguez',
  quote: 'Tenía miedo de que fuera otro plan complicado que iba a abandonar a la semana. Pero todo está tan bien explicado paso a paso que lo hice sin esfuerzo. Recuperé mi energía y mi confianza frente al espejo.',
  location: 'Mendoza, Argentina'
}, {
  name: 'Julieta Gómez',
  quote: 'Probé de todo para deshincharme y nada me funcionaba a largo plazo. Este protocolo no solo me desinfló, me enseñó a mantenerlo. Por la plata que vale es la mejor inversión que hice en mi salud.',
  location: 'La Plata, Argentina'
}];
function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
}
export function TestimonialsSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number, dir: number) => {
    setDirection(dir);
    setIndex(prev => {
      const total = TESTIMONIALS.length;
      return (next + total) % total;
    });
  }, []);
  const handlePrev = () => goTo(index - 1, -1);
  const handleNext = () => goTo(index + 1, 1);
  // Auto-advance
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setDirection(1);
      setIndex(prev => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(t);
  }, [paused]);
  const current = TESTIMONIALS[index];
  const variants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 60 : -60
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -60 : 60
    })
  };
  return <section className="py-20 bg-stone-50" data-id="element-403">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-404">
        {/* Heading */}
        <div className="text-center mb-12" data-id="element-405">
          <span className="inline-flex items-center gap-1.5 py-1.5 px-4 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold tracking-wide mb-4" data-id="element-406">
            <Star className="w-4 h-4 fill-emerald-600 text-emerald-600" data-id="element-407" />
            Clientas reales
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-408">
            Lo que dicen nuestras clientas
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto" data-id="element-409">
            Descubrí cómo el Protocolo Anti-Inflamación ayudó a desinflamarse y
            recuperar la confianza a mujeres como vos
          </p>
        </div>

        {/* Carousel */}
        <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-410">
          <div className="relative bg-white rounded-3xl shadow-xl shadow-stone-900/5 border border-stone-100 px-6 py-10 md:px-12 md:py-12 min-h-[340px] flex items-center overflow-hidden" data-id="element-411">
            <AnimatePresence mode="wait" custom={direction} data-id="element-412">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="w-full text-center" data-id="element-413">
                {/* Avatar */}
                <div className="flex justify-center mb-4" data-id="element-414">
                  <div className="w-16 h-16 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xl font-bold" data-id="element-415">
                    {getInitials(current.name)}
                  </div>
                </div>

                {/* Name + verified */}
                <div className="flex items-center justify-center gap-1.5 mb-2" data-id="element-416">
                  <span className="font-bold text-stone-900 text-lg" data-id="element-417">
                    {current.name}
                  </span>
                  <BadgeCheck className="w-5 h-5 text-emerald-500" data-id="element-418" />
                </div>

                {/* Stars */}
                <div className="flex items-center justify-center gap-0.5 mb-5" data-id="element-419">
                  {Array.from({
                  length: 5
                }).map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" data-id="element-420" />)}
                </div>

                {/* Quote */}
                <blockquote className="text-stone-700 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-5 italic" data-id="element-421">
                  "{current.quote}"
                </blockquote>

                {/* Location */}
                <p className="text-sm text-stone-400 font-medium" data-id="element-422">
                  {current.location}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Arrows */}
          <button type="button" onClick={handlePrev} aria-label="Testimonio anterior" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 md:-translate-x-1/2 w-11 h-11 rounded-full bg-white border border-stone-200 shadow-md flex items-center justify-center text-stone-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" data-id="element-423">
            <ChevronLeft className="w-5 h-5" data-id="element-424" />
          </button>
          <button type="button" onClick={handleNext} aria-label="Testimonio siguiente" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 md:translate-x-1/2 w-11 h-11 rounded-full bg-white border border-stone-200 shadow-md flex items-center justify-center text-stone-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" data-id="element-425">
            <ChevronRight className="w-5 h-5" data-id="element-426" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-6" data-id="element-427">
          {TESTIMONIALS.map((_, i) => <button key={i} type="button" onClick={() => goTo(i, i > index ? 1 : -1)} aria-label={`Ir al testimonio ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-8 bg-emerald-600' : 'w-2 bg-stone-300 hover:bg-stone-400'}`} data-id="element-428" />)}
        </div>
      </div>
    </section>;
}